import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import ChipList from '@features/filter/ChipList';
import ChipModal from '@features/filter/ChipModal';
import { isDetailsPage } from '@features/filter/static/filterUtil';
import { useAddTrendResearchPersonalSettingsMutation } from '@features/trend-research/api/onboardingApi';

import {
	clearGlobalFilterWithExcludeField,
	removeItemFromGlobalFilter,
	updateTextItemInGlobalFilter
} from '@store/actions/FilterAction';

import { checkAnyUserType } from '@utils/Util';

import { useAddPersonalSettingMutation } from '@/api/commonApis';

const Chip = ({ maxChar = 100, parentComponent, entityId, entityType, settingsKey }) => {
	const filterFields = useSelector((store) => store.filterFieldStore);
	const [chipList, setChipList] = useState([]);
	const dispatch = useDispatch();
	const [pageParam, setPageParam] = useState(null);
	const [isShowRemainingModal, setIsShowRemainingModal] = useState(false);
	const location = useLocation();
	const [addTrendResearchPersonalSettings] = useAddTrendResearchPersonalSettingsMutation();
	const [addPersonalSetting] = useAddPersonalSettingMutation();

	const debounce = useDebouncedCallback(() => {
		if (location.pathname === '/trends') {
			addTrendResearchPersonalSettings({
				requestBody: {
					key: 'TREND_FILTER',
					value: JSON.stringify({ ...filterFields, trendResearchSearch: [] })
				}
			});
		}
	}, 200);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const queryParam = params.get('active');
		if (queryParam === 'ORDER_DASHBOARD') {
			setPageParam('ORDER_DASHBOARD');
		}
	}, [location.search]);

	useEffect(() => {
		const cloneFilterFields = isDetailsPage() ? { ...filterFields?.DETAILS_PAGE_FILTERS } : filterFields;
		let fields = [];
		for (let field in cloneFilterFields) {
			if (cloneFilterFields[field]?.length > 0) {
				cloneFilterFields[field].forEach((item) => {
					if (!('isHideChip' in item) || !item.isHideChip) {
						fields.push(item);
					}
				});
			}
		}
		setChipList(fields);
	}, [filterFields]);

	useEffect(() => {
		if (
			isDetailsPage() &&
			parentComponent !== 'FAVOURITE_DETAILS' &&
			parentComponent !== 'COLLECTION_DETAILS'
		) {
			let fields = [];
			for (let field in filterFields) {
				if (filterFields[field]?.length > 0) {
					filterFields[field].forEach((item) => {
						if (item.name === 'stages' || item.name === 'statusList' || item.name === 'tags') {
							fields.push(item);
						}
					});
				}
			}
			setChipList(fields);
		}
	}, [filterFields]);

	useEffect(() => {
		const filterBarElement = document.getElementById('filter-bar-section-id');
		if (filterBarElement) {
			if (chipList.length > 0) {
				filterBarElement.classList.add('chip-added');
			} else {
				filterBarElement.classList.remove('chip-added');
			}
		}
	}, [chipList]);

	const clearAllFilter = () => {
		dispatch(clearGlobalFilterWithExcludeField(['availabilityStatusList', 'status']));
		if (pageParam === 'ORDER_DASHBOARD') {
			let value = !checkAnyUserType(['ADMIN', 'PROJECT_DIRECTOR'])
				? 'order.id,desc'
				: 'tnaStatus,order.id,desc';
			let data = {
				name: 'sort',
				type: 'text',
				key: value,
				value: value === 'order.id,desc' ? 'Group by order' : 'Group by TNA status',
				identifier: 'sort-order'
			};

			dispatch(updateTextItemInGlobalFilter(data));

			//dispatch(updateTextItemMultipleInGlobalFilter(data));
		}
		if (parentComponent === 'FAVOURITE_DETAILS' || parentComponent === 'COLLECTION_DETAILS') {
			addPersonalSetting({
				body: {
					key: settingsKey,
					value: JSON.stringify({}),
					entityId: entityId,
					entityType: entityType
				}
			});
		}
	};

	const removeItemFromFilter = (chip) => {
		dispatch(removeItemFromGlobalFilter(chip));
		let newList = chipList?.filter(
			(item) => item?.key !== chip?.key && item?.categoryId === chip?.categoryId
		);
		const ifAllExist = (list) => list?.some((item) => item?.value === 'All');
		if (newList?.length <= 2 && ifAllExist(newList)) {
			dispatch(removeItemFromGlobalFilter(newList?.find((item) => item?.value === 'All')));
		}
		if (parentComponent === 'FAVOURITE_DETAILS' || parentComponent === 'COLLECTION_DETAILS') {
			addPersonalSetting({
				body: {
					key: settingsKey,
					value: JSON.stringify(filterFields?.DETAILS_PAGE_FILTERS),
					entityId: entityId,
					entityType: entityType
				}
			});
		}
		debounce();
	};

	const onShowChipModal = () => {
		setIsShowRemainingModal(true);
	};

	const onCloseChipModal = () => {
		setIsShowRemainingModal(false);
	};

	const isShowClearAll = () => {
		const excludedChips = chipList.filter(
			(item) =>
				!['status', 'isBuyerRequest', 'availabilityStatusList'].includes(item.name) &&
				!(item.name === 'search' && !item.key)
		);
		return excludedChips.length > 0;
	};

	const calculateMaxLength = (list, maxLength) => {
		if (!list.length) return;
		let totalLength = 0;
		let sliceIndex = 0;

		for (let i = 0; i < list?.length; i++) {
			totalLength += list[i]?.value?.length;
			if (totalLength > maxLength) {
				break;
			}
			sliceIndex = i + 1;
		}

		return sliceIndex;
	};

	const isShowMore = () => {
		for (let chip of chipList) {
			if (chip.name === 'status' || chip.name === 'isBuyerRequest') {
				return false;
			}
		}
		return true;
	};

	const maxSliceLength = calculateMaxLength(chipList, maxChar);

	const filteredChipList = chipList?.filter((item) => item.name !== 'sort' && item.value !== '');

	return (
		<>
			<div className="chip-container d-flex align-items-center flex-wrap">
				<div className="chips d-flex align-items-center flex-wrap">
					<ChipList
						chipList={chipList.slice(0, maxSliceLength)}
						removeItemFromFilter={removeItemFromFilter}
					/>
					{chipList.length > maxSliceLength && isShowMore() ? (
						<div className="chip cursor-pointer" onClick={onShowChipModal}>
							<span>+{chipList.slice(maxSliceLength)?.length} more</span>
						</div>
					) : null}
					{filteredChipList?.length > 0 && isShowClearAll() ? (
						<span
							className="clear"
							onClick={(e) => {
								e.stopPropagation();
								clearAllFilter();
								if (location.pathname === '/trends') {
									addTrendResearchPersonalSettings({
										requestBody: {
											key: 'TREND_FILTER',
											value: JSON.stringify({})
										}
									});
								}
							}}
						>
							Clear all
						</span>
					) : null}
				</div>
			</div>
			{isShowRemainingModal && chipList.length ? (
				<ChipModal
					chipList={chipList}
					removeItemFromFilter={removeItemFromFilter}
					onClose={onCloseChipModal}
					onClearAll={clearAllFilter}
				/>
			) : null}
		</>
	);
};

export default Chip;
